import { z } from 'zod';

//https://docs.vapi.ai/call-forwarding#call-transfers-mode
export enum VoiceCallTransferCallMode {
  // The assistant forwards the call to the destination without any message or summary.
  BlindTransfer = 'BlindTransfer',
  // The assistant forwards the call to the destination and adds a SIP header X-Transfer-Summary to the call to include the summary.
  BlindTransferAddSummaryToSipHeader = 'BlindTransferAddSummaryToSipHeader',
  // The assistant dials the destination, delivers the message to the destination party, connects the customer, and leaves the call.
  WarmTransferSayMessage = 'WarmTransferSayMessage',
  //The assistant dials the destination, provides a summary of the call to the destination party, connects the customer, and leaves the call.
  WarmTransferSaySummary = 'WarmTransferSaySummary',
  //The assistant dials the destination, waits for the operator to speak, delivers the message to the destination party, and then connects the customer.
  WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage = 'WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage',
  //The assistant dials the destination, waits for the operator to speak, provides a summary of the call to the destination party, and then connects the customer.
  WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary = 'WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary',
  // The assistant dials the destination, executes the twiml instructions on the destination call leg, connects the customer, and leaves the call
  WarmTransferTwiml = 'WarmTransferTwiml',
  // The assistant puts the customer on hold, dials the destination, and if the destination answers (and is human), delivers a message or summary before connecting the customer.
  // If the destination is unreachable or not human (e.g., with voicemail detection), the assistant delivers the fallbackMessage to the customer and optionally ends the call
  WarmTransferExperimental = 'WarmTransferExperimental',
  // The assistant dials the destination, sends an 10-digit call token via DTMF. Customer can then query notable's call api with from, to and token to get the call info.
  TransferWithDtmfCallToken = 'TransferWithDtmfCallToken',
}

export enum VoiceCallTransferCallToolDestinationType {
  Number = 'Number',
  Sip = 'Sip',
}

// Number destination schemas - each mode with number-specific fields
const BaseNumberDestinationSchema = z.object({
  type: z.literal(VoiceCallTransferCallToolDestinationType.Number),
  number: z.string().min(1),
  description: z.string(),
  callerIdOverride: z.string().optional(),
  extension: z.string().optional(),
});

const TransferWithDtmfCallTokenNumberSchema = BaseNumberDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.TransferWithDtmfCallToken),
});

const BlindTransferNumberSchema = BaseNumberDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.BlindTransfer),
  customerMessage: z.string().optional(),
});

const WarmTransferSayMessageNumberSchema = BaseNumberDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.WarmTransferSayMessage),
  customerMessage: z.string().optional(),
  destinationMessage: z.string().optional(),
});

const WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessageNumberSchema =
  BaseNumberDestinationSchema.extend({
    transferMode: z.literal(
      VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage
    ),
    customerMessage: z.string().optional(),
    destinationMessage: z.string().optional(),
  });

const WarmTransferSaySummaryNumberSchema = BaseNumberDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.WarmTransferSaySummary),
  customerMessage: z.string().optional(),
  summaryMessagesSystemPrompt: z.string().optional(),
});

const WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummaryNumberSchema =
  BaseNumberDestinationSchema.extend({
    transferMode: z.literal(
      VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary
    ),
    customerMessage: z.string().optional(),
    summaryMessagesSystemPrompt: z.string().optional(),
  });

// SIP destination schemas - each mode with SIP-specific fields
const BaseSipDestinationSchema = z.object({
  type: z.literal(VoiceCallTransferCallToolDestinationType.Sip),
  sipUri: z.string().min(1),
  description: z.string(),
});

const BlindTransferSipSchema = BaseSipDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.BlindTransfer),
  customerMessage: z.string().optional(),
});

const BlindTransferAddSummaryToSipHeaderSipSchema = BaseSipDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader),
  customerMessage: z.string().optional(),
  summaryMessagesSystemPrompt: z.string().optional(),
});

const WarmTransferSayMessageSipSchema = BaseSipDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.WarmTransferSayMessage),
  customerMessage: z.string().optional(),
  destinationMessage: z.string().optional(),
});

const WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessageSipSchema =
  BaseSipDestinationSchema.extend({
    transferMode: z.literal(
      VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage
    ),
    customerMessage: z.string().optional(),
    destinationMessage: z.string().optional(),
  });

const WarmTransferSaySummarySipSchema = BaseSipDestinationSchema.extend({
  transferMode: z.literal(VoiceCallTransferCallMode.WarmTransferSaySummary),
  customerMessage: z.string().optional(),
  summaryMessagesSystemPrompt: z.string().optional(),
});

const WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummarySipSchema =
  BaseSipDestinationSchema.extend({
    transferMode: z.literal(
      VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary
    ),
    customerMessage: z.string().optional(),
    summaryMessagesSystemPrompt: z.string().optional(),
  });

const VoiceCallTransferCallNumberDestinationSchema = z.discriminatedUnion('transferMode', [
  TransferWithDtmfCallTokenNumberSchema,
  BlindTransferNumberSchema,
  WarmTransferSayMessageNumberSchema,
  WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessageNumberSchema,
  WarmTransferSaySummaryNumberSchema,
  WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummaryNumberSchema,
]);

const VoiceCallTransferCallSipDestinationSchema = z.discriminatedUnion('transferMode', [
  BlindTransferSipSchema,
  BlindTransferAddSummaryToSipHeaderSipSchema,
  WarmTransferSayMessageSipSchema,
  WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessageSipSchema,
  WarmTransferSaySummarySipSchema,
  WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummarySipSchema,
]);

export const VoiceCallTransferCallToolDestinationSchema = z.union([
  VoiceCallTransferCallNumberDestinationSchema,
  VoiceCallTransferCallSipDestinationSchema,
]);

export type VoiceCallTransferCallToolDestination = z.infer<
  typeof VoiceCallTransferCallToolDestinationSchema
>;
